import DOMPurify from 'dompurify';

/**
 * The platform's single HTML sanitizer.
 *
 * H-1 — WHY THIS EXISTS.
 *
 * Before this, five near-identical hand-rolled `DOMParser` functions did the
 * job, and a sixth only pretended to:
 *
 *   sanitizeArticleHtml       apps/customer-portal + apps/web (byte-identical clones)
 *   sanitizeRichTextHtml      packages/ui/src/editor/RichTextEditor.tsx (NOT exported,
 *                             which is exactly why the clones exist)
 *   sanitizeHtml              apps/web ReplyEditor.tsx (weaker: omits <form>)
 *   sanitizeMessageHtml       apps/web ConversationThread.tsx — REMOVED NOTHING
 *                             DANGEROUS despite the name
 *
 * `sanitizeMessageHtml` sat on the main ticket-message render path and stripped
 * no `<script>`, no `on*` handlers, no `javascript:` URLs. `ReplyEditor.tsx`
 * relied on it in writing ("The rendered conversation thread sanitizes again on
 * its side"), so the misleading name had already misled a second author. Four
 * further sinks had no wrapper at all.
 *
 * Message content in a service desk originates from inbound email and chat, so
 * it is attacker-controlled by design: mail a crafted HTML body to a monitored
 * address, the ticket is created automatically, and script executes when an
 * agent opens it — in a session holding staff privileges across the tenant.
 *
 * `rules.md 8.7` already required DOMPurify. This is that requirement, in one
 * place, exported.
 *
 * ---
 *
 * FAILS CLOSED. Every hand-rolled predecessor returned the RAW INPUT from its
 * `catch` (`sanitizeMessageHtml` and `ReplyEditor`'s copy) — so a parse failure
 * delivered unsanitized markup to `dangerouslySetInnerHTML`, precisely when
 * something unusual was going on. This returns `''` instead.
 *
 * SSR-SAFE. DOMPurify needs a DOM. Under SSR (and in any non-browser context)
 * there is no safe way to sanitize, so this returns `''` rather than passing
 * markup through unchecked.
 */

/** Attributes an email body legitimately uses for layout. */
const EMAIL_LAYOUT_ATTRS = [
  'align', 'valign', 'bgcolor', 'color', 'width', 'height',
  'cellpadding', 'cellspacing', 'border', 'colspan', 'rowspan',
];

export interface SanitizeOptions {
  /**
   * Allow the inline `style` attribute and layout attributes.
   *
   * Needed for email bodies and knowledge-base articles, which carry their
   * formatting inline. DOMPurify still sanitizes the CSS itself, so
   * `expression()`, `url(javascript:…)` and friends do not survive — but inline
   * style remains a CSS-exfiltration surface, so it is opt-in per call site
   * rather than on by default.
   */
  allowStyles?: boolean;
}

/**
 * Sanitize untrusted HTML for rendering via `dangerouslySetInnerHTML`.
 *
 * Returns `''` when the input is empty, when no DOM is available, or when
 * sanitization throws.
 */
export function sanitizeHtml(html: string, options: SanitizeOptions = {}): string {
  if (!html || typeof html !== 'string') return '';

  // No DOM (SSR, a worker, a test env without jsdom) -> nothing can be made
  // safe here, so emit nothing rather than passing markup through.
  if (typeof window === 'undefined' || typeof (window as any).document === 'undefined') {
    return '';
  }

  try {
    return DOMPurify.sanitize(html, {
      // Anything not named here is dropped. An allowlist, not a blocklist:
      // the predecessors removed a fixed list of tags, which meant every
      // element they had not thought of survived.
      ALLOWED_TAGS: [
        'a', 'b', 'blockquote', 'br', 'caption', 'code', 'div', 'em', 'figure',
        'figcaption', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img',
        'li', 'ol', 'p', 'pre', 'q', 's', 'small', 'span', 'strike', 'strong',
        'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr',
        'u', 'ul',
      ],
      ALLOWED_ATTR: [
        'href', 'src', 'alt', 'title', 'target', 'rel',
        'loading', 'referrerpolicy',
        'class', 'dir', 'lang',
        ...(options.allowStyles ? ['style', ...EMAIL_LAYOUT_ATTRS] : []),
      ],
      // Only these URL schemes survive on href/src. This is what rejects
      // `javascript:`, and also `data:text/html`, which several of the
      // hand-rolled copies missed entirely while explicitly checking for
      // `javascript:`.
      ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|cid:|data:image\/(?:png|jpe?g|gif|webp|bmp);base64,)/i,
      // Belt and braces: these are excluded by the allowlist above, but naming
      // them makes the intent greppable and survives an ALLOWED_TAGS edit.
      FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form',
                    'input', 'button', 'link', 'meta', 'base', 'svg', 'math'],
      FORBID_ATTR: ['srcdoc', 'formaction', 'ping', 'autofocus'],
      // Reject `<html>`/`<body>` wrappers rather than adopting them.
      WHOLE_DOCUMENT: false,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
    }) as unknown as string;
  } catch {
    return '';
  }
}

/**
 * Sanitize an email or ticket-message body.
 *
 * Inline styles are permitted because email bodies carry their formatting
 * inline and would otherwise render as unstyled text.
 */
export function sanitizeMessageHtml(html: string): string {
  return sanitizeHtml(html, { allowStyles: true });
}

/**
 * Sanitize knowledge-base article HTML.
 *
 * Same profile as messages — articles are authored in the rich-text editor and
 * rely on inline styling too.
 */
export function sanitizeArticleHtml(html: string): string {
  return sanitizeHtml(html, { allowStyles: true });
}
